import * as moment from 'moment-timezone';

import { getAge } from '../utils';
import { Event } from './event';
import { EventType } from './event-type';
import { ScheduleType } from './event-type-enums';
import { WaitlistEntry } from './scheduled-event';

/** The `bypass_restriction` codes for an event type's age band. */
export enum AgeRestrictionCode {
  minAge = 'min_age',
  maxAge = 'max_age',
}

export type AgeRestrictedEventType = Pick<EventType, 'schedule_type' | 'min_participant_age' | 'max_participant_age'>;

export interface AgeRestrictionResult {
  age: number;
  violations: AgeRestrictionCode[];
}

/**
 * The age in whole years and the limits it falls outside: too young below the
 * minimum, too old above the maximum, as the server decides it. Null when the
 * birthday is unknown, which the server does not block. `asOfDate` is
 * `YYYY-MM-DD`, as for `getAge`.
 */
export function checkAgeRestriction(birthday: string | null | undefined,
                                    minAge: number | null | undefined,
                                    maxAge: number | null | undefined,
                                    asOfDate?: string): AgeRestrictionResult | null {
  if (!birthday) { return null; }

  const age = getAge(birthday, asOfDate);

  const violations: AgeRestrictionCode[] = [];
  if ((minAge !== null) && (minAge !== undefined) && (age < minAge)) {
    violations.push(AgeRestrictionCode.minAge);
  }
  if ((maxAge !== null) && (maxAge !== undefined) && (age > maxAge)) {
    violations.push(AgeRestrictionCode.maxAge);
  }

  return { age, violations };
}

/**
 * The date the server measures an event's age band at: the first part's start
 * date, already in the location's zone.
 */
export function eventAgeAsOfDate(event: Event): string | undefined {
  if (event.event_parts.length === 0) { return undefined; }
  return event.event_parts[0].start_datetime_isoformat.substring(0, 10);
}

/**
 * The date the server measures an event type waitlist's age band at: the
 * current date in the organization's zone, or the browser's without a known zone.
 */
export function eventTypeAgeAsOfDate(orgTimeZone: string | null | undefined): string {
  const now = (orgTimeZone && moment.tz.zone(orgTimeZone)) ? moment().tz(orgTimeZone) : moment();
  return now.format('YYYY-MM-DD');
}

/** The birthday the server checks for a waitlist entry: the linked customer's current one when given. */
export function waitlistEntryAgeBirthday(entry: Pick<WaitlistEntry, 'birthday' | 'participant_customer_birthday'>): string | null {
  return entry.participant_customer_birthday || entry.birthday || null;
}

export interface AgeRestrictionParticipant {
  key: string;
  name: string;
  birthday: string | null | undefined;
}

export interface AgeRestrictionViolation {
  participantKey: string;
  name: string;
  age: number;
  code: AgeRestrictionCode;
  limit: number;
}

/** One row per participant per limit they fall outside. Rolling event types are never checked. */
export function ageRestrictionViolations(participants: AgeRestrictionParticipant[],
                                         eventType: AgeRestrictedEventType,
                                         asOfDate?: string): AgeRestrictionViolation[] {
  if (eventType.schedule_type === ScheduleType.rolling) { return []; }

  const rows: AgeRestrictionViolation[] = [];
  for (const participant of participants) {
    const result = checkAgeRestriction(
      participant.birthday,
      eventType.min_participant_age,
      eventType.max_participant_age,
      asOfDate
    );
    if (result === null) { continue; }

    for (const code of result.violations) {
      rows.push({
        participantKey: participant.key,
        name: participant.name,
        age: result.age,
        code,
        limit: (code === AgeRestrictionCode.minAge)
          ? eventType.min_participant_age
          : eventType.max_participant_age,
      });
    }
  }
  return rows;
}

/**
 * A send modal's age-band warning and the acknowledgement that unblocks it.
 * Any change to the target or to the warned rows clears the acknowledgement, so
 * a tick never covers a participant staff were not shown.
 */
export class AgeRestrictionBypass {
  public violations: AgeRestrictionViolation[] = [];
  public acknowledged = false;

  private inputsKey: string | null = null;
  private violationsKey: string | null = null;

  /**
   * Recheck against the current selection. `target` identifies the event or
   * event type being sent to; pass a null `eventType` while it is unknown.
   */
  public refresh(target: string,
                 participants: AgeRestrictionParticipant[],
                 eventType: AgeRestrictedEventType | null,
                 asOfDate?: string) {
    const inputsKey = JSON.stringify([
      target,
      participants,
      eventType && [eventType.schedule_type, eventType.min_participant_age, eventType.max_participant_age],
      asOfDate,
    ]);
    if (inputsKey === this.inputsKey) { return; }
    this.inputsKey = inputsKey;

    const violations = eventType ? ageRestrictionViolations(participants, eventType, asOfDate) : [];

    const violationsKey = JSON.stringify([target, violations]);
    if (violationsKey === this.violationsKey) { return; }
    this.violationsKey = violationsKey;
    this.violations = violations;
    this.acknowledged = false;
  }

  public blocksSend(): boolean {
    return (this.violations.length > 0) && !this.acknowledged;
  }

  /** The codes to send for one participant; empty when they are within the band. */
  public codesFor(participantKey: string): AgeRestrictionCode[] {
    return this.violations
      .filter(row => row.participantKey === participantKey)
      .map(row => row.code);
  }
}
